/**
 * Public PDF viewer for the restaurant menu.
 * Accessible at /menu-restaurant — designed to be scanned via QR
 * from physical tables. No header, no footer, full-screen PDF.
 *
 * The PDF URL is fetched fresh on every load (no aggressive caching)
 * so updating the document in the admin panel reflects immediately.
 */
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Download, Loader2 } from "lucide-react";

const MENU_DOC_ID = "doc_bc5d765ee1be";
// Always go through the production document API; the doc lives in production.
const PDF_URL = `https://aravell-golf.emergent.host/api/documents/file/${MENU_DOC_ID}`;
// Cache-busting query string forces a fresh response on every load
const buildLiveUrl = () => `${PDF_URL}?t=${Date.now()}`;

export default function MenuRestaurantPage() {
  const [pdfUrl, setPdfUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPdfUrl(buildLiveUrl());
  }, []);

  return (
    <div
      data-testid="menu-restaurant-page"
      className="fixed inset-0 bg-neutral-950 flex flex-col"
    >
      <Helmet>
        <title>Carta del Restaurant – Aravell Golf Club</title>
        <meta
          name="description"
          content="Carta del Restaurant d'Aravell Golf Club. Consulta el menú actualitzat."
        />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
        <meta name="theme-color" content="#0a0a0a" />
      </Helmet>

      {/* Slim top bar with download button */}
      <header
        className="flex items-center justify-between px-3 py-2 bg-neutral-900/95 backdrop-blur border-b border-neutral-800"
        data-testid="menu-restaurant-topbar"
      >
        <h1 className="text-white text-sm font-medium tracking-wide truncate">
          Carta del Restaurant
        </h1>
        <a
          href={PDF_URL}
          download="carta-restaurant-aravell-golf.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-neutral-900 text-xs font-semibold hover:bg-neutral-200 transition-colors shrink-0"
          data-testid="menu-restaurant-download"
        >
          <Download className="w-3.5 h-3.5" />
          <span>Descarregar</span>
        </a>
      </header>

      {/* PDF viewer */}
      <div className="flex-1 relative bg-neutral-900">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-white pointer-events-none">
            <Loader2 className="w-7 h-7 animate-spin opacity-70" />
          </div>
        )}
        {pdfUrl && (
          <iframe
            key={pdfUrl}
            src={pdfUrl}
            title="Carta del Restaurant Aravell Golf Club"
            className="w-full h-full border-0"
            onLoad={() => setLoading(false)}
            data-testid="menu-restaurant-iframe"
            // `view=FitH` makes the PDF fit the page width on browsers
            // that honor it (mostly desktop Chrome/Edge); mobile browsers
            // simply ignore it.
          />
        )}
      </div>

      {/* Fallback link for browsers without inline PDF support (very old iOS) */}
      <noscript>
        <a
          href={PDF_URL}
          className="text-white underline absolute inset-0 flex items-center justify-center"
        >
          Veure la carta del restaurant
        </a>
      </noscript>
    </div>
  );
}
